import { useState, useEffect } from 'react'

/**
 * The `useDebounce` function is a custom hook in TypeScript that takes a value and a number of
 * milliseconds, and returns a debounced value that is updated after the specified delay.
 * @param {T} value - The value that you want to debounce. This can be of any type.
 * @param {number} milliSeconds - The `milliSeconds` parameter is the number of milliseconds to wait
 * before updating the debounced value.
 * @returns The `useDebounce` hook returns the debounced value.
 */
export const useDebounce = <T>(value: T, milliSeconds: number) => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value)
    }, milliSeconds)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [value, milliSeconds])

  return debouncedValue
}

export default useDebounce
